// Тактическая доска (FR-BOARD-01…09, 12): поле в метрах эталонного поля, viewBox сохраняет пропорции.
// Подвижные объекты обновляются напрямую через атрибут transform (без перерисовки React) — 60 кадров/с.
import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, type Ref } from "react";
import type { FieldGeometry, Overlay, Segment } from "@brovka/content-types";
import type { Point, Pose } from "./timeline.ts";
import { zoneRects } from "./zones.ts";

export type PieceInfo = {
  id: string;
  kind: "us" | "them" | "ball" | "cone" | "label";
  label?: string;
  hero?: boolean;
  goalkeeper?: boolean;
};

export type ZoneLayer = {
  cols: number;
  rows: number;
  cellM: number;
  cells: ArrayLike<number>;
  /** Показывать «не получится» (только родительский режим, UI-UX 3.1). */
  showBad?: boolean;
};

export type BoardHandle = {
  /** Поставить объекты в позу (вызывается в каждом кадре анимации). */
  setPose(pose: Pose): void;
};

export type BoardProps = {
  field: Pick<FieldGeometry, "lengthM" | "widthM" | "goals" | "shootingZoneM" | "penaltyArea">;
  pieces: PieceInfo[];
  pose: Pose;
  arrows?: Segment[];
  overlays?: Overlay[];
  zone?: ZoneLayer | null;
  highlight?: string[];
  /** Герой, которого можно ставить на поле (задание «куда встать»). */
  placeHero?: string | null;
  onPlace?: (p: Point, touch: boolean) => void;
  /** Можно нажимать на игроков (задание «кому пас»). */
  pickable?: string[];
  onPickPlayer?: (id: string) => void;
  /** Поле повёрнуто вертикально (узкий экран, книжная ориентация). */
  rotated?: boolean;
  /** Рамка «Так не надо» для неправильной ветки. */
  wrong?: boolean;
  /** Подписи и фишки крупнее — детский режим. */
  kid?: boolean;
  ariaLabel: string;
  ref?: Ref<BoardHandle>;
  className?: string;
};

const PAD = 1.5;

export function Board(props: BoardProps) {
  const { field, pieces, pose, rotated = false } = props;
  const L = field.lengthM;
  const W = field.widthM;
  const groupRef = useRef<SVGGElement>(null);
  const nodes = useRef(new Map<string, SVGGElement>());
  const poseRef = useRef<Pose>(pose);
  const r = Math.max(0.5, Math.min(L, 60) * 0.042) * (props.kid ? 1.15 : 1);
  const toM = useCallback((p: Point): Point => [(p[0] / 100) * L, (p[1] / 100) * W], [L, W]);

  const apply = useCallback(
    (p: Pose) => {
      poseRef.current = p;
      for (const [id, pos] of Object.entries(p.bodies)) {
        const n = nodes.current.get(id);
        if (n) {
          const [x, y] = toM(pos);
          n.setAttribute("transform", `translate(${x.toFixed(3)} ${y.toFixed(3)})`);
        }
      }
      const b = nodes.current.get("__ball");
      if (b) {
        const [x, y] = toM(p.ball);
        const off = p.ballAttached ? r * 0.8 : 0;
        b.setAttribute("transform", `translate(${(x + off).toFixed(3)} ${(y + off * 0.6).toFixed(3)})`);
      }
    },
    [toM, r],
  );

  useImperativeHandle(props.ref, () => ({ setPose: apply }), [apply]);
  useEffect(() => apply(pose), [pose, apply]);

  // Ввод: перетаскивание героя оценивается по центру, касание — с допуском у границы зон (UI-UX 6.3).
  const drag = useRef<{ id: string; moved: boolean; start: [number, number] } | null>(null);
  const tap = useRef<{ x: number; y: number } | null>(null);
  const toNorm = (e: React.PointerEvent): Point | null => {
    const g = groupRef.current;
    const ctm = g?.getScreenCTM();
    if (!g || !ctm) return null;
    const pt = new DOMPoint(e.clientX, e.clientY).matrixTransform(ctm.inverse());
    return [Math.min(100, Math.max(0, (pt.x / L) * 100)), Math.min(100, Math.max(0, (pt.y / W) * 100))];
  };
  const onPointerDown = (e: React.PointerEvent<SVGSVGElement>) => {
    if (!props.placeHero || !props.onPlace) return;
    const target = (e.target as Element).closest("[data-piece]");
    if (target?.getAttribute("data-piece") === props.placeHero) {
      drag.current = { id: props.placeHero, moved: false, start: [e.clientX, e.clientY] };
      (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
    } else {
      tap.current = { x: e.clientX, y: e.clientY };
    }
  };
  const onPointerMove = (e: React.PointerEvent<SVGSVGElement>) => {
    const d = drag.current;
    if (!d) return;
    if (Math.hypot(e.clientX - d.start[0], e.clientY - d.start[1]) > 6) d.moved = true;
    const p = toNorm(e);
    if (p && d.moved) apply({ ...poseRef.current, bodies: { ...poseRef.current.bodies, [d.id]: p } });
  };
  const onPointerUp = (e: React.PointerEvent<SVGSVGElement>) => {
    const p = toNorm(e);
    if (drag.current) {
      const moved = drag.current.moved;
      drag.current = null;
      if (moved && p) props.onPlace?.(p, false);
      return;
    }
    if (tap.current && p && Math.hypot(e.clientX - tap.current.x, e.clientY - tap.current.y) < 12) props.onPlace?.(p, true);
    tap.current = null;
  };

  const zoneEls = useMemo(() => {
    const z = props.zone;
    if (!z) return null;
    const rects = zoneRects(z.cells, z.cols, z.rows, z.cellM, (v) => v === 1 || v === 2 || (Boolean(z.showBad) && v === 0));
    return rects.map((q, i) => (
      <rect key={i} x={q.x} y={q.y} width={q.w + 0.01} height={q.h + 0.01} fill={q.v === 2 ? "var(--zone-great)" : q.v === 1 ? "var(--zone-ok)" : "var(--zone-bad)"} />
    ));
  }, [props.zone]);

  const vbW = L + 2 * PAD;
  const vbH = W + 2 * PAD;
  const viewBox = rotated ? `${-PAD} ${-PAD} ${vbH} ${vbW}` : `${-PAD} ${-PAD} ${vbW} ${vbH}`;
  // Повёрнутое поле: атака снизу вверх.
  const fieldTransform = rotated ? `translate(0 ${L}) rotate(-90)` : undefined;
  const lw = Math.max(0.08, L * 0.006);
  const pickable = new Set(props.pickable ?? []);
  const highlight = new Set(props.highlight ?? []);
  const shown = (p: Point) => toM(p);

  return (
    <svg
      className={props.className}
      viewBox={viewBox}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={props.ariaLabel}
      style={{ touchAction: props.placeHero ? "none" : "manipulation", display: "block", width: "100%", height: "100%" }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
    >
      <defs>
        <pattern id="brovka-hatch" width={0.35} height={0.35} patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <rect width={0.35} height={0.35} fill="var(--team-them)" />
          <line x1={0} y1={0} x2={0} y2={0.35} stroke="#fff" strokeWidth={0.1} />
        </pattern>
        <marker id="brovka-arrow" viewBox="0 0 10 10" refX={8} refY={5} markerWidth={4} markerHeight={4} orient="auto-start-reverse">
          <path d="M0,0 L10,5 L0,10 z" fill="#fff" />
        </marker>
        <marker id="brovka-arrow-attack" viewBox="0 0 10 10" refX={8} refY={5} markerWidth={3} markerHeight={3} orient="auto">
          <path d="M0,0 L10,5 L0,10 z" fill="rgba(255,255,255,0.85)" />
        </marker>
      </defs>
      <g ref={groupRef} transform={fieldTransform}>
        <rect x={-PAD} y={-PAD} width={vbW} height={vbH} fill="var(--pitch)" />
        <rect x={0} y={0} width={L} height={W} fill="none" stroke="var(--pitch-line)" strokeWidth={lw} />
        <line x1={L / 2} y1={0} x2={L / 2} y2={W} stroke="var(--pitch-line)" strokeWidth={lw} />
        <circle cx={L / 2} cy={W / 2} r={Math.min(W * 0.15, 9.15)} fill="none" stroke="var(--pitch-line)" strokeWidth={lw} />
        {field.shootingZoneM ? (
          <>
            <line x1={field.shootingZoneM} y1={0} x2={field.shootingZoneM} y2={W} stroke="var(--pitch-line)" strokeWidth={lw} strokeDasharray="0.5 0.35" />
            <line x1={L - field.shootingZoneM} y1={0} x2={L - field.shootingZoneM} y2={W} stroke="var(--pitch-line)" strokeWidth={lw} strokeDasharray="0.5 0.35" />
          </>
        ) : null}
        {field.penaltyArea ? (
          <>
            <rect x={0} y={(W - field.penaltyArea.widthM) / 2} width={field.penaltyArea.depthM} height={field.penaltyArea.widthM} fill="none" stroke="var(--pitch-line)" strokeWidth={lw} />
            <rect x={L - field.penaltyArea.depthM} y={(W - field.penaltyArea.widthM) / 2} width={field.penaltyArea.depthM} height={field.penaltyArea.widthM} fill="none" stroke="var(--pitch-line)" strokeWidth={lw} />
          </>
        ) : null}
        {field.goals.map((g) => {
          const gy = (g.centerY / 100) * W;
          const own = g.side === "left";
          // Ворота соперника визуально отличаются: цвет команды соперника и толще (FR-BOARD-09).
          return (
            <rect
              key={g.id}
              x={own ? -0.7 : L}
              y={gy - g.widthM / 2}
              width={0.7}
              height={g.widthM}
              fill={own ? "var(--team-us)" : "var(--team-them)"}
              stroke="#fff"
              strokeWidth={own ? lw : lw * 2}
            />
          );
        })}
        <line x1={L * 0.38} y1={W + PAD * 0.55} x2={L * 0.62} y2={W + PAD * 0.55} stroke="rgba(255,255,255,0.85)" strokeWidth={Math.max(0.15, lw * 1.8)} markerEnd="url(#brovka-arrow-attack)">
          <title>Атакуем сюда</title>
        </line>
        <g aria-hidden="true" shapeRendering="crispEdges">{zoneEls}</g>
        {(props.overlays ?? []).map((o, i) => {
          if (o.type === "shadow") return <polygon key={i} points={o.polygon.map((p) => shown(p).join(",")).join(" ")} fill="var(--shadow-zone)" />;
          if (o.type === "zone")
            return <polygon key={i} points={o.polygon.map((p) => shown(p).join(",")).join(" ")} fill={o.zone === "danger" ? "var(--zone-bad)" : "var(--zone-great)"} />;
          if (o.type === "passLine") {
            const a = pose.bodies[o.from];
            const b = pose.bodies[o.to];
            if (!a || !b) return null;
            const [x1, y1] = shown(a);
            const [x2, y2] = shown(b);
            return (
              <g key={i}>
                <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={o.open ? "#36B37E" : "#D64545"} strokeWidth={r * 0.9} strokeOpacity={0.55} strokeLinecap="round" />
                {!o.open ? <text x={(x1 + x2) / 2} y={(y1 + y2) / 2} fontSize={r * 1.6} textAnchor="middle" dominantBaseline="central" fill="#fff">✕</text> : null}
              </g>
            );
          }
          return null;
        })}
        {(props.arrows ?? []).filter((s) => s.kind !== "carry" && s.path.length > 1).map((s, i) => {
          const d = s.path.map((p, k) => `${k ? "L" : "M"}${shown(p as Point).join(",")}`).join(" ");
          const style =
            s.kind === "run" ? { dash: `${r * 0.8} ${r * 0.6}`, w: r * 0.22 } : s.kind === "dribble" ? { dash: `${r * 0.25} ${r * 0.25}`, w: r * 0.32 } : s.kind === "shot" ? { dash: undefined, w: r * 0.5 } : { dash: undefined, w: r * 0.28 };
          return <path key={i} d={d} fill="none" stroke="#fff" strokeOpacity={0.85} strokeWidth={style.w} strokeDasharray={style.dash} markerEnd="url(#brovka-arrow)" />;
        })}
        {pieces.map((p) => {
          if (p.kind === "ball") return null;
          const pos = pose.bodies[p.id];
          if (!pos) return null;
          const [x, y] = toM(pos);
          const isHero = Boolean(p.hero);
          const canPick = pickable.has(p.id);
          return (
            <g
              key={p.id}
              data-piece={p.id}
              ref={(el) => {
                if (el) nodes.current.set(p.id, el);
                else nodes.current.delete(p.id);
              }}
              transform={`translate(${x} ${y})`}
              style={{ cursor: props.placeHero === p.id || canPick ? "pointer" : undefined }}
              onClick={canPick ? () => props.onPickPlayer?.(p.id) : undefined}
              role={canPick ? "button" : undefined}
              aria-label={canPick ? `Игрок ${p.label ?? p.id}` : undefined}
            >
              {props.placeHero === p.id || canPick ? <circle r={r * 2.4} fill="transparent" /> : null}
              {isHero ? <circle r={r * 1.55} fill="var(--hero)" opacity={0.75} className={props.placeHero === p.id ? "brovka-pulse" : undefined} /> : null}
              {highlight.has(p.id) ? <circle r={r * 1.8} fill="none" stroke="var(--hero)" strokeWidth={r * 0.25} /> : null}
              {p.kind === "us" ? <circle r={r} fill="var(--team-us)" stroke="#fff" strokeWidth={r * 0.14} /> : null}
              {p.kind === "them" ? <circle r={r} fill="url(#brovka-hatch)" stroke="#5a1a06" strokeWidth={r * 0.32} /> : null}
              {p.kind === "cone" ? <polygon points={`0,${-r * 0.8} ${-r * 0.65},${r * 0.55} ${r * 0.65},${r * 0.55}`} fill="#ff8a00" /> : null}
              {p.kind === "label" ? (
                <text fontSize={r * 1.1} textAnchor="middle" dominantBaseline="central" fill="#fff">
                  {p.label}
                </text>
              ) : null}
              {(p.kind === "us" || p.kind === "them") && (p.label || p.goalkeeper) ? (
                <text
                  fontSize={r * 1.05}
                  fontWeight={800}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fill="#fff"
                  stroke="rgba(0,0,0,0.6)"
                  strokeWidth={r * 0.08}
                  style={{ fontVariantNumeric: "tabular-nums", pointerEvents: "none" }}
                >
                  {p.goalkeeper && !p.label ? "В" : p.label}
                </text>
              ) : null}
            </g>
          );
        })}
        <g
          ref={(el) => {
            if (el) nodes.current.set("__ball", el);
            else nodes.current.delete("__ball");
          }}
          style={{ pointerEvents: "none" }}
        >
          <circle r={r * 0.5} fill="#fff" stroke="#111" strokeWidth={r * 0.12} />
          <circle r={r * 0.18} fill="#111" />
        </g>
      </g>
      {props.wrong ? <rect x={-PAD} y={-PAD} width={rotated ? vbH : vbW} height={rotated ? vbW : vbH} fill="none" stroke="var(--wrong)" strokeWidth={PAD * 0.5} /> : null}
    </svg>
  );
}
